import { OwsAbacusContractClient } from "@abacus/ows-contract-client/fetch";
import { SAPSyncContractBatchConfig } from "./types";
import { OwsAbacusEventClient } from "@abacus/ows-event-client/fetch";
import { Chunk, Logger } from "@abacus/common";
import { OwsAbacusStateClient } from "@abacus/ows-state-client/fetch";
import { ChunkRunResult, Runner } from "@/types";
import { Contract } from "@abacus/ows-contract-client";
import { SAPSyncContractTask } from "./sapSyncContractTask";
import { Throttler } from "@abacus/throttlers";

const DAY_MS = 24 * 60 * 60 * 1000;

export class SAPSyncContractBatch implements Runner<Chunk<number>, ChunkRunResult<void>> {
  contractClient: OwsAbacusContractClient;
  eventClient: OwsAbacusEventClient;
  logger?: Logger;
  resync: boolean;
  stateClient: OwsAbacusStateClient;
  tasker: SAPSyncContractTask;
  throttler?: Throttler;

  constructor({
    contractClient,
    eventClient,
    logger,
    resync,
    stateClient,
    throttler,
  }: SAPSyncContractBatchConfig) {
    this.contractClient = contractClient;
    this.eventClient = eventClient;
    this.logger = logger;
    this.resync = resync ?? false;
    this.stateClient = stateClient;
    this.tasker = new SAPSyncContractTask({
        contractClient,
        eventClient,
        logger,
        resync: true,
        stateClient,
        throttler,
    });
    this.throttler = throttler;
  }

  async run(chunk: Chunk<number>): Promise<ChunkRunResult<void>> {
    // Get contract map
    const { data: contracts } = await this.contractClient.getContracts(chunk.values);
    const contractMap = new Map<number, Contract>();
    for (const contract of contracts) {
        contractMap.set(contract.contract_id, contract);
    }

    // Initialize output
    const out: ChunkRunResult<void> = {
        chunkIndex: chunk.chunkIndex,
        errors: [],
        startIndex: chunk.startIndex,
        success: 0,
        total: chunk.values.length,
        values: []
    };

    // For each contract ID
    const contractIds = chunk.values;
    for (let index = 0; index < contractIds.length; ++index) {
      try {
        // Check if the contract exists
        const contract = contractMap.get(contractIds[index]);
        if (contract == null) {
          throw new ReferenceError("Contract does not exist");
        }

        // Check if the contract is already synced
        const lastModified = new Date(contract.last_modified ?? "");
        const sapCreatedAt = new Date(contract.sap_created_at ?? "");
        const diffDays = (lastModified.getTime() - sapCreatedAt.getTime()) / DAY_MS;
        const isSynced = !isNaN(diffDays) && diffDays < 1;

        // Sync if needed
        if (!isSynced || this.resync) {
            await this.tasker.run(contract.contract_id);
        } else {
            this.logger?.info(
                `Already synced:`,
                `Contract ID: ${contract.contract_id},`,
                `Synced: ${sapCreatedAt.toISOString()}`
            );
        }

        // Output result
        out.values.push([index + chunk.startIndex, undefined]);
      } catch (error) {
        // Output error
        out.errors.push([index + chunk.startIndex, (error as Error).message]);
      }
    }

    // Return results
    out.success = out.values.length;
    return out;
  }
}